import { z } from 'zod';
import { INTEGRATION_TYPES } from '../integrations.constants';

export const PREVENTION_CONTROL_TYPES = {
  WAF: 'waf',
  NETWORK: 'network',
  CONFIG: 'config',
  IPS: 'ips',
} as const;

export type PreventionControlType =
  (typeof PREVENTION_CONTROL_TYPES)[keyof typeof PREVENTION_CONTROL_TYPES];

export const PreventionControlTypeSchema = z.enum([
  PREVENTION_CONTROL_TYPES.WAF,
  PREVENTION_CONTROL_TYPES.NETWORK,
  PREVENTION_CONTROL_TYPES.CONFIG,
  PREVENTION_CONTROL_TYPES.IPS,
]);

export const PREVENTION_CONTROL_STATUSES = {
  GENERATING: 'generating',
  STAGED: 'staged',
  ACTIVATING: 'activating',
  ACTIVE: 'active',
  ADVISORY: 'advisory',
  REJECTED: 'rejected',
  FAILED: 'failed',
  REMOVED: 'removed',
  EXPIRED: 'expired',
  DRIFTED: 'drifted',
} as const;

export type PreventionControlStatus =
  (typeof PREVENTION_CONTROL_STATUSES)[keyof typeof PREVENTION_CONTROL_STATUSES];

export const PreventionControlStatusSchema = z.enum([
  PREVENTION_CONTROL_STATUSES.GENERATING,
  PREVENTION_CONTROL_STATUSES.STAGED,
  PREVENTION_CONTROL_STATUSES.ACTIVATING,
  PREVENTION_CONTROL_STATUSES.ACTIVE,
  PREVENTION_CONTROL_STATUSES.ADVISORY,
  PREVENTION_CONTROL_STATUSES.REJECTED,
  PREVENTION_CONTROL_STATUSES.FAILED,
  PREVENTION_CONTROL_STATUSES.REMOVED,
  PREVENTION_CONTROL_STATUSES.EXPIRED,
  PREVENTION_CONTROL_STATUSES.DRIFTED,
]);

export const PreventionProviderSchema = z.enum([
  INTEGRATION_TYPES.GENERIC_WAF,
  INTEGRATION_TYPES.AWS_SECURITY_GROUP,
  INTEGRATION_TYPES.GCP_FIREWALL,
  INTEGRATION_TYPES.AZURE_NSG,
  INTEGRATION_TYPES.GENERIC_CONFIG,
  INTEGRATION_TYPES.GENERIC_IPS,
]);

export type PreventionProvider = z.infer<typeof PreventionProviderSchema>;

export const CloudProviderSchema = z.enum([
  INTEGRATION_TYPES.AWS,
  INTEGRATION_TYPES.GCP,
  INTEGRATION_TYPES.AZURE,
]);

export type CloudProvider = z.infer<typeof CloudProviderSchema>;

export const WAF_MATCH_TARGETS = {
  PATH: 'path',
  QUERY_PARAM: 'queryParam',
  QUERY_STRING: 'queryString',
  BODY: 'body',
  HEADER: 'header',
  METHOD: 'method',
} as const;

export type WafMatchTarget =
  (typeof WAF_MATCH_TARGETS)[keyof typeof WAF_MATCH_TARGETS];

export const WafMatchTargetSchema = z.enum([
  WAF_MATCH_TARGETS.PATH,
  WAF_MATCH_TARGETS.QUERY_PARAM,
  WAF_MATCH_TARGETS.QUERY_STRING,
  WAF_MATCH_TARGETS.BODY,
  WAF_MATCH_TARGETS.HEADER,
  WAF_MATCH_TARGETS.METHOD,
]);

export const WAF_MATCH_OPERATORS = {
  EQUALS: 'equals',
  CONTAINS: 'contains',
  STARTS_WITH: 'startsWith',
  REGEX: 'regex',
  SQLI_MATCH: 'sqliMatch',
  XSS_MATCH: 'xssMatch',
} as const;

export const WafMatchOperatorSchema = z.enum([
  WAF_MATCH_OPERATORS.EQUALS,
  WAF_MATCH_OPERATORS.CONTAINS,
  WAF_MATCH_OPERATORS.STARTS_WITH,
  WAF_MATCH_OPERATORS.REGEX,
  WAF_MATCH_OPERATORS.SQLI_MATCH,
  WAF_MATCH_OPERATORS.XSS_MATCH,
]);

const WAF_OPERATORS_WITHOUT_VALUE = new Set<string>([
  WAF_MATCH_OPERATORS.SQLI_MATCH,
  WAF_MATCH_OPERATORS.XSS_MATCH,
]);

export const WAF_MATCH_TRANSFORMS = {
  NONE: 'none',
  LOWERCASE: 'lowercase',
  URL_DECODE: 'urlDecode',
  HTML_DECODE: 'htmlDecode',
  COMPRESS_WHITESPACE: 'compressWhitespace',
} as const;

export const WafMatchTransformSchema = z.enum([
  WAF_MATCH_TRANSFORMS.NONE,
  WAF_MATCH_TRANSFORMS.LOWERCASE,
  WAF_MATCH_TRANSFORMS.URL_DECODE,
  WAF_MATCH_TRANSFORMS.HTML_DECODE,
  WAF_MATCH_TRANSFORMS.COMPRESS_WHITESPACE,
]);

export const WAF_ACTIONS = {
  COUNT: 'count',
  LOG: 'log',
  CHALLENGE: 'challenge',
  BLOCK: 'block',
} as const;

export const WafActionSchema = z.enum([
  WAF_ACTIONS.COUNT,
  WAF_ACTIONS.LOG,
  WAF_ACTIONS.CHALLENGE,
  WAF_ACTIONS.BLOCK,
]);

export const WafMatchConditionSchema = z
  .strictObject({
    target: WafMatchTargetSchema,
    key: z.string().min(1).optional(),
    operator: WafMatchOperatorSchema,
    value: z.string().min(1).optional(),
    transform: WafMatchTransformSchema.optional(),
  })
  .superRefine((condition, ctx) => {
    const requiresKey =
      condition.target === WAF_MATCH_TARGETS.QUERY_PARAM ||
      condition.target === WAF_MATCH_TARGETS.HEADER;

    if (requiresKey && !condition.key) {
      ctx.addIssue({
        code: 'custom',
        message: `key is required when target is "${condition.target}"`,
        path: ['key'],
      });
    }

    const requiresValue = !WAF_OPERATORS_WITHOUT_VALUE.has(condition.operator);

    if (requiresValue && !condition.value) {
      ctx.addIssue({
        code: 'custom',
        message: `value is required when operator is "${condition.operator}"`,
        path: ['value'],
      });
    }
  });

export type WafMatchCondition = z.infer<typeof WafMatchConditionSchema>;

export const WafControlSpecSchema = z.object({
  controlType: z.literal(PREVENTION_CONTROL_TYPES.WAF),
  matchConditions: z.array(WafMatchConditionSchema).min(1),
  action: WafActionSchema,
  ttlSeconds: z.int().positive().optional(),
  rationale: z.string().optional(),
});

export type WafControlSpec = z.infer<typeof WafControlSpecSchema>;

export const NetworkControlSpecSchema = z.object({
  controlType: z.literal(PREVENTION_CONTROL_TYPES.NETWORK),
  targetIp: z.string().min(1),
  targetPort: z.int().min(1).max(65535),
  protocol: z.enum(['tcp', 'udp', 'icmp', 'all']).default('tcp'),
  action: z.literal('deny'),
  direction: z.enum(['inbound', 'outbound']).default('inbound'),
  scopeId: z.string().optional(),
  scopeType: z.enum(['securityGroup', 'nacl', 'firewall', 'nsg']).optional(),
  cidr: z.string().optional(),
  ttlSeconds: z.int().positive().optional(),
  rationale: z.string().optional(),
});

export type NetworkControlSpec = z.infer<typeof NetworkControlSpecSchema>;

export const ConfigControlSpecSchema = z.object({
  controlType: z.literal(PREVENTION_CONTROL_TYPES.CONFIG),
  target: z.string().min(1),
  change: z.enum([
    'enforceTls',
    'disablePublicAccess',
    'dnsRecord',
    'emailAuth',
  ]),
  details: z.record(z.string(), z.unknown()).optional(),
  ttlSeconds: z.int().positive().optional(),
  rationale: z.string().optional(),
});

export type ConfigControlSpec = z.infer<typeof ConfigControlSpecSchema>;

export const IpsControlSpecSchema = z.object({
  controlType: z.literal(PREVENTION_CONTROL_TYPES.IPS),
  signatureId: z.string().min(1),
  action: z.enum(['drop', 'reject', 'alert']),
  direction: z.enum(['inbound', 'outbound', 'both']).default('inbound'),
  protocol: z.enum(['tcp', 'udp', 'icmp', 'all']).default('tcp'),
  targetIp: z.string().optional(),
  cidr: z.string().optional(),
  sensorScope: z.string().optional(),
  ttlSeconds: z.int().positive().optional(),
  rationale: z.string().optional(),
});

export type IpsControlSpec = z.infer<typeof IpsControlSpecSchema>;

export const PreventionControlSpecSchema = z.discriminatedUnion('controlType', [
  WafControlSpecSchema,
  NetworkControlSpecSchema,
  ConfigControlSpecSchema,
  IpsControlSpecSchema,
]);

export type PreventionControlSpec = z.infer<typeof PreventionControlSpecSchema>;

export const PREVENTION_WAF_VENDORS = {
  GENERIC: 'generic',
  CLOUDFLARE: 'cloudflare',
  AWS_WAFV2: 'awsWafv2',
  VERCEL: 'vercel',
} as const;

export type PreventionWafVendor =
  (typeof PREVENTION_WAF_VENDORS)[keyof typeof PREVENTION_WAF_VENDORS];

export const PreventionWafVendorSchema = z.enum([
  PREVENTION_WAF_VENDORS.GENERIC,
  PREVENTION_WAF_VENDORS.CLOUDFLARE,
  PREVENTION_WAF_VENDORS.AWS_WAFV2,
  PREVENTION_WAF_VENDORS.VERCEL,
]);

export const PREVENTION_COVERAGE_KINDS = {
  DOMAIN: 'domain',
  ZONE: 'zone',
  CIDR: 'cidr',
  CLOUD_ACCOUNT: 'cloudAccount',
  VPC: 'vpc',
} as const;

export const PreventionCoverageKindSchema = z.enum([
  PREVENTION_COVERAGE_KINDS.DOMAIN,
  PREVENTION_COVERAGE_KINDS.ZONE,
  PREVENTION_COVERAGE_KINDS.CIDR,
  PREVENTION_COVERAGE_KINDS.CLOUD_ACCOUNT,
  PREVENTION_COVERAGE_KINDS.VPC,
]);

export const PREVENTION_SCOPE_TYPES = {
  SECURITY_GROUP: 'securityGroup',
  NACL: 'nacl',
  FIREWALL: 'firewall',
  NSG: 'nsg',
  ZONE: 'zone',
  WEB_ACL: 'webAcl',
  SENSOR: 'sensor',
} as const;

export const PreventionScopeTypeSchema = z.enum([
  PREVENTION_SCOPE_TYPES.SECURITY_GROUP,
  PREVENTION_SCOPE_TYPES.NACL,
  PREVENTION_SCOPE_TYPES.FIREWALL,
  PREVENTION_SCOPE_TYPES.NSG,
  PREVENTION_SCOPE_TYPES.ZONE,
  PREVENTION_SCOPE_TYPES.WEB_ACL,
  PREVENTION_SCOPE_TYPES.SENSOR,
]);

export type PreventionScopeType = z.infer<typeof PreventionScopeTypeSchema>;

export const PREVENTION_TARGET_MATCH_KINDS = {
  APPLICATION: 'application',
  DOMAIN: 'domain',
  SURFACE: 'surface',
} as const;

export const PreventionTargetMatchKindSchema = z.enum([
  PREVENTION_TARGET_MATCH_KINDS.APPLICATION,
  PREVENTION_TARGET_MATCH_KINDS.DOMAIN,
  PREVENTION_TARGET_MATCH_KINDS.SURFACE,
]);

export const PreventionCoverageSchema = z.object({
  kind: PreventionCoverageKindSchema,
  value: z.string().min(1),
  scopeRef: z.string().min(1).optional(),
  scopeType: PreventionScopeTypeSchema.optional(),
});

export type PreventionCoverage = z.infer<typeof PreventionCoverageSchema>;

export const PreventionConnectBodySchema = z.object({
  apiKey: z.string().min(1).optional(),
  accountId: z.string().min(1).optional(),
  zoneId: z.string().min(1).optional(),
  baseUrl: z.url().optional(),
  label: z.string().min(1).optional(),
  vendor: PreventionWafVendorSchema.optional(),
  isInternal: z.boolean().optional(),
  twingateNetworkId: z.string().min(1).optional(),
  sensorScope: z.string().min(1).optional(),
  coverage: z.array(PreventionCoverageSchema).optional(),
});

export type PreventionConnectBody = z.infer<typeof PreventionConnectBodySchema>;

export const MAX_CLOUD_INVENTORY_SURFACES = 20;

export const PreventionConnectionDtoSchema = z.object({
  id: z.string(),
  controlType: PreventionControlTypeSchema,
  provider: PreventionProviderSchema,
  vendor: PreventionWafVendorSchema.nullable(),
  label: z.string(),
  isInternal: z.boolean(),
  twingateNetworkId: z.string().nullable(),
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED']),
  coverage: z.array(PreventionCoverageSchema),
  stagePreventionEnabled: z.boolean(),
  externalNetworkInventoryEnabled: z.boolean(),
  externalNetworkDnsEnabled: z.boolean(),
  inventorySurfaceIds: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PreventionConnectionDto = z.infer<
  typeof PreventionConnectionDtoSchema
>;

export const PreventionConnectionIdParamSchema = z.object({
  connectionId: z.string().min(1),
});

export const UpdatePreventionConnectionBodySchema = z.object({
  label: z.string().min(1).optional(),
  vendor: PreventionWafVendorSchema.optional(),
  isInternal: z.boolean().optional(),
  twingateNetworkId: z.string().min(1).nullable().optional(),
  coverage: z.array(PreventionCoverageSchema).optional(),
  stagePreventionEnabled: z.boolean().optional(),
  externalNetworkInventoryEnabled: z.boolean().optional(),
  externalNetworkDnsEnabled: z.boolean().optional(),
  inventorySurfaceIds: z
    .array(z.string().min(1))
    .max(MAX_CLOUD_INVENTORY_SURFACES)
    .optional(),
});

export type UpdatePreventionConnectionBody = z.infer<
  typeof UpdatePreventionConnectionBodySchema
>;

export const PreventionTargetMappingDtoSchema = z.object({
  id: z.string(),
  matchKind: PreventionTargetMatchKindSchema,
  matchValue: z.string(),
  connectionId: z.string(),
  scopeRef: z.string().nullable(),
  scopeType: PreventionScopeTypeSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PreventionTargetMappingDto = z.infer<
  typeof PreventionTargetMappingDtoSchema
>;

export const CreatePreventionTargetMappingBodySchema = z.object({
  matchKind: PreventionTargetMatchKindSchema,
  matchValue: z.string().min(1),
  connectionId: z.string().min(1),
  scopeRef: z.string().min(1).optional(),
  scopeType: PreventionScopeTypeSchema.optional(),
});

export type CreatePreventionTargetMappingBody = z.infer<
  typeof CreatePreventionTargetMappingBodySchema
>;

export const PreventionTargetMappingIdParamSchema = z.object({
  mappingId: z.string().min(1),
});

export const ListPreventionTargetMappingsQuerySchema = z.object({
  matchKind: PreventionTargetMatchKindSchema.optional(),
  matchValue: z.string().min(1).optional(),
});

export type ListPreventionTargetMappingsQuery = z.infer<
  typeof ListPreventionTargetMappingsQuerySchema
>;

export const PreventionListConnectionsQuerySchema = z.object({
  controlType: PreventionControlTypeSchema.optional(),
  provider: PreventionProviderSchema.optional(),
});

export type PreventionListConnectionsQuery = z.infer<
  typeof PreventionListConnectionsQuerySchema
>;

export const PreventionApprovalRecordSchema = z.object({
  userId: z.string(),
  at: z.string(),
});

export const AwsCloudConnectBodySchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.AWS),
  roleArn: z.string().min(1),
  externalId: z.string().min(1),
  region: z.string().min(1).optional(),
});

export const GcpCloudConnectBodySchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.GCP),
  serviceAccountEmail: z.email(),
  projectId: z.string().min(1),
  region: z.string().min(1).optional(),
});

export const AzureCloudConnectBodySchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.AZURE),
  tenantId: z.string().min(1),
  clientId: z.string().min(1),
  subscriptionId: z.string().min(1),
  region: z.string().min(1).optional(),
});

export const CloudConnectBodySchema = z.discriminatedUnion('provider', [
  AwsCloudConnectBodySchema,
  GcpCloudConnectBodySchema,
  AzureCloudConnectBodySchema,
]);

export type CloudConnectBody = z.infer<typeof CloudConnectBodySchema>;
export type AwsCloudConnectBody = z.infer<typeof AwsCloudConnectBodySchema>;
export type GcpCloudConnectBody = z.infer<typeof GcpCloudConnectBodySchema>;
export type AzureCloudConnectBody = z.infer<typeof AzureCloudConnectBodySchema>;

export const AwsCloudProviderMetaSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.AWS),
  roleArn: z.string().min(1),
  externalId: z.string().min(1),
  region: z.string().optional(),
});

export const GcpCloudProviderMetaSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.GCP),
  serviceAccountEmail: z.email(),
  projectId: z.string().min(1),
  region: z.string().optional(),
});

export const AzureCloudProviderMetaSchema = z.object({
  provider: z.literal(INTEGRATION_TYPES.AZURE),
  tenantId: z.string().min(1),
  clientId: z.string().min(1),
  subscriptionId: z.string().min(1),
  region: z.string().optional(),
});

export const CloudProviderMetaSchema = z.discriminatedUnion('provider', [
  AwsCloudProviderMetaSchema,
  GcpCloudProviderMetaSchema,
  AzureCloudProviderMetaSchema,
]);

export type CloudProviderMeta = z.infer<typeof CloudProviderMetaSchema>;

export const PreventionProviderParamSchema = z.object({
  controlType: PreventionControlTypeSchema,
  provider: PreventionProviderSchema,
});

export const CloudProviderParamSchema = z.object({
  provider: CloudProviderSchema,
});

export const PreventionControlIdParamSchema = z.object({
  controlId: z.string().min(1),
});

export const PreventionControlDtoSchema = z.object({
  id: z.string(),
  findingId: z.string(),
  controlType: PreventionControlTypeSchema,
  provider: PreventionProviderSchema,
  connectionId: z.string().nullable(),
  scopeRef: z.string().nullable(),
  scopeType: PreventionScopeTypeSchema.nullable(),
  status: PreventionControlStatusSchema,
  controlRef: z.string().nullable(),
  spec: PreventionControlSpecSchema.nullable(),
  preview: z.record(z.string(), z.unknown()).nullable(),
  generatedBy: z.record(z.string(), z.unknown()).nullable(),
  stagedAt: z.string().nullable(),
  stagedBy: z.string().nullable(),
  approvals: z.array(PreventionApprovalRecordSchema),
  approvedBy: z.array(z.string()),
  requiredApprovals: z.int().positive(),
  activatedBy: z.string().nullable(),
  activatedAt: z.string().nullable(),
  rejectedBy: z.string().nullable(),
  deactivatedBy: z.string().nullable(),
  expiresAt: z.string().nullable(),
  lastReconciledAt: z.string().nullable(),
  rollbackRef: z.string().nullable(),
  url: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type PreventionControlDto = z.infer<typeof PreventionControlDtoSchema>;

export const PreventionControlTestProbeKindSchema = z.enum([
  'attack',
  'clean',
  'custom',
]);

export type PreventionControlTestProbeKind = z.infer<
  typeof PreventionControlTestProbeKindSchema
>;

export const PreventionControlTestProbeInputSchema = z.object({
  kind: PreventionControlTestProbeKindSchema.default('custom'),
  url: z.string().min(1),
  method: z.string().min(1),
  headers: z.record(z.string(), z.string()).optional(),
  body: z.string().optional(),
});

export type PreventionControlTestProbeInput = z.infer<
  typeof PreventionControlTestProbeInputSchema
>;

export const PreventionControlTestBodySchema = z.object({
  probes: z.array(PreventionControlTestProbeInputSchema).min(1).max(3),
});

export type PreventionControlTestBody = z.infer<
  typeof PreventionControlTestBodySchema
>;

export const PreventionControlTestTemplateSchema = z.object({
  attack: PreventionControlTestProbeInputSchema,
  clean: PreventionControlTestProbeInputSchema,
  routingHint: z.string().nullable(),
});

export type PreventionControlTestTemplate = z.infer<
  typeof PreventionControlTestTemplateSchema
>;

export const PreventionControlTestProbeResultSchema = z.object({
  kind: PreventionControlTestProbeKindSchema,
  url: z.string(),
  method: z.string(),
  statusCode: z.number().nullable(),
  blocked: z.boolean(),
  durationMs: z.int().nonnegative(),
  error: z.string().nullable(),
  requestHeaders: z.record(z.string(), z.string()),
  requestBody: z.string().nullable(),
  responseHeaders: z.record(z.string(), z.string()),
  responseBody: z.string().nullable(),
});

export type PreventionControlTestProbeResult = z.infer<
  typeof PreventionControlTestProbeResultSchema
>;

export const PreventionControlTestResultSchema = z.object({
  passed: z.boolean().nullable(),
  summary: z.string(),
  routingHint: z.string().nullable().optional(),
  probes: z.array(PreventionControlTestProbeResultSchema).min(1),
});

export type PreventionControlTestResult = z.infer<
  typeof PreventionControlTestResultSchema
>;

export const PreventionConnectionStatusResponseSchema = z.object({
  controlType: PreventionControlTypeSchema,
  provider: PreventionProviderSchema,
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  stagePreventionEnabled: z.boolean(),
});

export type PreventionConnectionStatusResponse = z.infer<
  typeof PreventionConnectionStatusResponseSchema
>;

export const CloudConnectionStatusResponseSchema = z.object({
  provider: CloudProviderSchema,
  status: z.enum(['VALID', 'EXPIRED', 'REVOKED', 'NOT_CONNECTED']),
  principal: z.string().nullable(),
  accountRef: z.string().nullable(),
  region: z.string().nullable(),
  trustPrincipal: z.string().nullable().default(null),
  stagePreventionEnabled: z.boolean(),
  externalNetworkInventoryEnabled: z.boolean(),
  externalNetworkDnsEnabled: z.boolean(),
  inventorySurfaceIds: z.array(z.string()),
});

export type CloudConnectionStatusResponse = z.infer<
  typeof CloudConnectionStatusResponseSchema
>;

export const CLOUD_TOGGLEABLE_FLOWS = {
  STAGE_PREVENTION_ON_FINDING: 'stage_prevention_on_finding',
  SYNC_EXTERNAL_NETWORK_INVENTORY: 'sync_external_network_inventory',
  SYNC_EXTERNAL_NETWORK_DNS: 'sync_external_network_dns',
} as const;

export const CloudToggleableFlowSchema = z.enum([
  CLOUD_TOGGLEABLE_FLOWS.STAGE_PREVENTION_ON_FINDING,
  CLOUD_TOGGLEABLE_FLOWS.SYNC_EXTERNAL_NETWORK_INVENTORY,
  CLOUD_TOGGLEABLE_FLOWS.SYNC_EXTERNAL_NETWORK_DNS,
]);

export type CloudToggleableFlow = z.infer<typeof CloudToggleableFlowSchema>;

export const CloudUpdateFlowsBodySchema = z.object({
  flow: CloudToggleableFlowSchema,
  enabled: z.boolean(),
});

export type CloudUpdateFlowsBody = z.infer<typeof CloudUpdateFlowsBodySchema>;

export const CloudUpdateInventorySettingsBodySchema = z.object({
  inventorySurfaceIds: z
    .array(z.string().min(1))
    .max(MAX_CLOUD_INVENTORY_SURFACES),
});

export type CloudUpdateInventorySettingsBody = z.infer<
  typeof CloudUpdateInventorySettingsBodySchema
>;

export const PreventionListQuerySchema = z.object({
  findingId: z.string().optional(),
  status: PreventionControlStatusSchema.optional(),
  controlType: PreventionControlTypeSchema.optional(),
});

export const PreventionActivateBodySchema = z.object({
  confirm: z.literal(true),
});

export type PreventionActivateBody = z.infer<
  typeof PreventionActivateBodySchema
>;

export const PreventionRejectBodySchema = z.object({
  reason: z.string().optional(),
});

export type PreventionRejectBody = z.infer<typeof PreventionRejectBodySchema>;

export const PreventionWafSuggestionVendorSchema = z.enum([
  PREVENTION_WAF_VENDORS.CLOUDFLARE,
  PREVENTION_WAF_VENDORS.AWS_WAFV2,
  PREVENTION_WAF_VENDORS.VERCEL,
]);

export const PreventionWafSuggestionSchema = z.object({
  vendor: PreventionWafSuggestionVendorSchema,
  confidence: z.enum(['high', 'medium', 'low']),
  evidence: z.string().min(1),
  suggestedScopeRef: z.string().min(1).optional(),
  hostname: z.string().min(1).optional(),
});

export type PreventionWafSuggestion = z.infer<
  typeof PreventionWafSuggestionSchema
>;

export const PreventionWafSuggestionsResponseSchema = z.object({
  suggestions: z.array(PreventionWafSuggestionSchema),
});

export type PreventionWafSuggestionsResponse = z.infer<
  typeof PreventionWafSuggestionsResponseSchema
>;

export const PreventionApplicationIdParamSchema = z.object({
  applicationId: z.string().min(1),
});

export const PreventionWafTargetSchema = z.object({
  ref: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
});

export type PreventionWafTarget = z.infer<typeof PreventionWafTargetSchema>;

export const DiscoverPreventionWafTargetsQuerySchema = z.object({
  vendor: PreventionWafVendorSchema,
  connectionId: z.string().min(1).optional(),
  regions: z.string().min(1).optional(),
});

export type DiscoverPreventionWafTargetsQuery = z.infer<
  typeof DiscoverPreventionWafTargetsQuerySchema
>;

export const DiscoverPreventionWafTargetsResponseSchema = z.object({
  targets: z.array(PreventionWafTargetSchema),
});

export type DiscoverPreventionWafTargetsResponse = z.infer<
  typeof DiscoverPreventionWafTargetsResponseSchema
>;
